import { useEffect, useMemo, useState } from 'react';
import {
    Control,
    Controller,
    FieldError,
    Path,
    FieldValues,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import container from 'src/container';
import { OrganizationGroup } from 'src/domain/organization';
import { MultiSelectList } from './MultiSelectList';
import { useNotification } from 'src/components/Notification/useNotfication';
import { buildGroupOptionTree } from './groupOptionTree';

const {
    cradle: { munduClient },
} = container;

export type ConsumerGroupsSelectProps<TFieldValues extends FieldValues> = {
    control: Control<TFieldValues>;
    name: Path<TFieldValues>;
    organizationId?: string;
    error?: FieldError;
    disabled?: boolean;
    allSelected?: boolean;
    groups?: OrganizationGroup[];
    allowedGroupIds?: string[];
    leafLabelSuffixByGroupId?: Record<string, string>;
};

export const ConsumerGroupsSelect = <TFieldValues extends FieldValues>({
    control,
    name,
    organizationId,
    error,
    disabled = false,
    allSelected = false,
    groups,
    allowedGroupIds,
    leafLabelSuffixByGroupId,
}: ConsumerGroupsSelectProps<TFieldValues>) => {
    const notify = useNotification();
    const { t } = useTranslation();
    const [organizationGroups, setOrganizationGroups] = useState<
        OrganizationGroup[]
    >(groups ?? []);
    const [isGroupsLoading, setIsGroupsLoading] = useState(false);
    const shouldFetchGroups = !groups;

    useEffect(() => {
        if (!shouldFetchGroups) {
            setOrganizationGroups(groups ?? []);
            return;
        }

        if (!organizationId) {
            setOrganizationGroups([]);
            return;
        }

        let isMounted = true;
        const fetchOrganizationGroups = async () => {
            try {
                setIsGroupsLoading(true);
                const organization = await munduClient.getOrganization(
                    organizationId,
                );
                if (isMounted) {
                    setOrganizationGroups(organization.groups ?? []);
                }
            } catch (error) {
                if (error instanceof Error) {
                    notify(error.message, true);
                }
            } finally {
                if (isMounted) {
                    setIsGroupsLoading(false);
                }
            }
        };

        fetchOrganizationGroups();

        return () => {
            isMounted = false;
        };
    }, [shouldFetchGroups, groups, organizationId, notify]);

    const filteredGroups = (() => {
        if (allowedGroupIds === undefined) {
            return organizationGroups;
        }

        if (!allowedGroupIds.length) {
            return [];
        }

        const allowedSet = new Set(allowedGroupIds);
        return organizationGroups.filter(group => allowedSet.has(group.id));
    })();

    const hasGroups = filteredGroups.length > 0;

    const groupOptions = useMemo(
        () =>
            buildGroupOptionTree(
                filteredGroups,
                leafLabelSuffixByGroupId
                    ? {
                          getLabel: (group, isLeaf) => {
                              const baseLabel = group.name?.trim().length
                                  ? group.name
                                  : group.id;
                              if (!isLeaf) {
                                  return baseLabel;
                              }
                              const suffix = leafLabelSuffixByGroupId[group.id];
                              return suffix
                                  ? `${baseLabel} - ${suffix}`
                                  : baseLabel;
                          },
                      }
                    : undefined,
            ),
        [filteredGroups, leafLabelSuffixByGroupId],
    );

    return (
        <Controller
            control={control}
            name={name}
            render={({ field }) => (
                <MultiSelectList
                    options={groupOptions}
                    value={field.value ?? []}
                    onChange={values => field.onChange(values)}
                    disabled={disabled || !hasGroups}
                    allSelected={allSelected}
                    error={error}
                    helperText={t('common.loading')}
                    placeholder={t('search.genericPlaceholder')}
                    noOptionsLabel={t('users.form.groupsEmpty')}
                    noMatchesLabel={t('users.form.groupsEmpty')}
                    selectedLabel={t('users.form.groupsPlaceholder')}
                    isLoading={isGroupsLoading && shouldFetchGroups}
                />
            )}
        />
    );
};
